/**
 * CreateRecurringOrderDialog Component
 *
 * Dialog for creating a new recurring order for a customer.
 * Allows quick "on the spot" creation from the customer detail modal.
 *
 * Features:
 * - Select days of week using RecurringOrderOptions
 * - Set end conditions (Never, On date, After N occurrences)
 * - Add products with quantities
 * - Creates order and generates 6 months of instances
 */

import { useState, useEffect } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import { Plus, X, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import Combobox from '@/components/ui/combobox'
import { toast } from 'sonner'
import RecurringOrderOptions from '../RecurringOrderOptions'
import { useDataCache } from '@/contexts/DataCacheContext'
import {
  createRecurringOrder,
  generateInstances
} from '../../services/recurringOrderService'
import { fetchCustomerBranches, fetchCustomers } from '../../supabase-client'

// Day abbreviations for generated names
const DAY_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * Generate an order name from days and products
 * Format: "Mon/Thu Grey Oyster" or "Mon/Thu Grey Oyster +1"
 */
function generateOrderName(days, items, products) {
  // Format days (convert ISO weekdays 1-7 to abbreviations)
  const dayNames = days
    .sort((a, b) => a - b)
    .map(d => DAY_ABBREV[d % 7]) // ISO 7 (Sunday) maps to index 0
    .join('/')

  // Get first product name
  const firstItem = items[0]
  const firstProduct = products.find(p => p.id === firstItem?.product_id)
  const productName = firstProduct?.strain || 'Delivery'

  // Build name with product count suffix if multiple
  if (items.length > 1) {
    return `${dayNames} ${productName} +${items.length - 1}`
  }

  return `${dayNames} ${productName}`
}

/**
 * ItemsEditor - Inline component for editing recurring order line items
 */
function ItemsEditor({ items, onChange, products, productTypes, disabled }) {
  const addItem = () => {
    onChange([
      ...items,
      {
        product_id: '',
        product_type_id: '',
        quantity: 1
      }
    ])
  }

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index))
  }

  const updateItem = (index, field, value) => {
    const updated = [...items]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Products</Label>

      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">No products added yet</p>
      )}

      {items.map((item, index) => (
        <div key={index} className="flex gap-2 items-center">
          {/* Product Select */}
          <Select
            value={item.product_id}
            onValueChange={(v) => updateItem(index, 'product_id', v)}
            disabled={disabled}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.strain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Product Type Select */}
          <Select
            value={item.product_type_id}
            onValueChange={(v) => updateItem(index, 'product_type_id', v)}
            disabled={disabled}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {productTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Quantity Input */}
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0.1"
              step="0.1"
              value={item.quantity}
              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="w-20"
            />
            <span className="text-sm text-muted-foreground">kg</span>
          </div>

          {/* Remove Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeItem(index)}
            disabled={disabled}
            className="p-2 h-8 w-8"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}

      {/* Add Item Button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addItem}
        disabled={disabled}
        className="gap-1"
      >
        <Plus className="w-4 h-4" />
        Add Product
      </Button>
    </div>
  )
}

/**
 * CreateRecurringOrderDialog - Main dialog component
 *
 * @param {Object} props
 * @param {string} [props.customerId] - Optional customer ID (if not provided, shows customer selector)
 * @param {string} [props.branchId] - Optional branch ID (uses default if not provided)
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onCreated - Callback after successful creation
 */
export default function CreateRecurringOrderDialog({
  customerId,
  branchId,
  open,
  onOpenChange,
  onCreated
}) {
  // Name state
  const [name, setName] = useState('')

  // Customer state (for when customerId not provided)
  const [customers, setCustomers] = useState([])
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || '')

  // Branch state
  const [branches, setBranches] = useState([])
  const [selectedBranchId, setSelectedBranchId] = useState(branchId || '')

  // Schedule state
  const [days, setDays] = useState([])
  const [endType, setEndType] = useState('indefinite')
  const [endDate, setEndDate] = useState(null)
  const [occurrences, setOccurrences] = useState(null)

  // Items state
  const [items, setItems] = useState([])

  // Products and product types for dropdowns
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [loadingData, setLoadingData] = useState(true)

  // Save state
  const [saving, setSaving] = useState(false)

  // Data cache for products and product types
  const { getProducts, getProductTypes } = useDataCache()

  /**
   * Load products, product types, customers (if needed), and branches when dialog opens
   */
  useEffect(() => {
    if (open) {
      // Reset form state
      setName('')
      setSelectedCustomerId(customerId || '')
      setSelectedBranchId(branchId || '')
      setBranches([])
      setDays([])
      setEndType('indefinite')
      setEndDate(null)
      setOccurrences(null)
      setItems([])

      // Build list of promises to fetch
      const promises = [
        getProducts(),
        getProductTypes()
      ]

      // If customerId provided, fetch branches for that customer
      // Otherwise, fetch all customers (branches will be fetched on customer selection)
      if (customerId) {
        promises.push(fetchCustomerBranches(customerId))
      } else {
        promises.push(fetchCustomers())
      }

      setLoadingData(true)
      Promise.all(promises)
        .then(([productsData, productTypesData, thirdData]) => {
          setProducts(productsData || [])
          setProductTypes(productTypesData || [])

          if (customerId) {
            // thirdData is branches
            setBranches(thirdData || [])
            // Auto-select first branch if none provided and only one exists
            if (!branchId && thirdData?.length === 1) {
              setSelectedBranchId(thirdData[0].id)
            }
          } else {
            // thirdData is customers
            setCustomers(thirdData || [])
          }
        })
        .catch((err) => {
          console.error('[CreateRecurringOrderDialog] Error loading data:', err)
          toast.error('Failed to load data')
        })
        .finally(() => {
          setLoadingData(false)
        })
    }
  }, [open, getProducts, getProductTypes, customerId, branchId])

  /**
   * Load branches when customer is selected (only when customerId prop not provided)
   */
  const handleCustomerChange = async (newCustomerId) => {
    setSelectedCustomerId(newCustomerId)
    setSelectedBranchId('')
    setBranches([])

    if (newCustomerId) {
      try {
        const branchesData = await fetchCustomerBranches(newCustomerId)
        setBranches(branchesData || [])
        // Auto-select first branch if only one exists
        if (branchesData?.length === 1) {
          setSelectedBranchId(branchesData[0].id)
        }
      } catch (err) {
        console.error('[CreateRecurringOrderDialog] Error loading branches:', err)
        toast.error('Failed to load branches')
      }
    }
  }

  /**
   * Validate form before saving
   */
  const validateForm = () => {
    // Require customer selection if not provided via prop
    if (!customerId && !selectedCustomerId) {
      toast.error('Please select a customer')
      return false
    }

    // Require branch selection if customer has multiple branches
    if (branches.length > 1 && !selectedBranchId) {
      toast.error('Please select a branch')
      return false
    }

    if (days.length === 0) {
      toast.error('Please select at least one day')
      return false
    }

    if (items.length === 0) {
      toast.error('Please add at least one product')
      return false
    }

    // Check each item has required fields
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (!item.product_id) {
        toast.error(`Please select a product for item ${i + 1}`)
        return false
      }
      if (!item.product_type_id) {
        toast.error(`Please select a product type for item ${i + 1}`)
        return false
      }
      if (!item.quantity || item.quantity <= 0) {
        toast.error(`Please enter a valid quantity for item ${i + 1}`)
        return false
      }
    }

    // Validate end date if type is until_date
    if (endType === 'until_date' && !endDate) {
      toast.error('Please select an end date')
      return false
    }

    // Validate occurrences if type is after_count
    if (endType === 'after_count' && (!occurrences || occurrences < 1)) {
      toast.error('Please enter a valid number of occurrences')
      return false
    }

    return true
  }

  /**
   * Handle create button click
   */
  const handleCreate = async () => {
    if (!validateForm()) return

    setSaving(true)

    try {
      // Use provided customerId or selected one
      const effectiveCustomerId = customerId || selectedCustomerId

      // Format items for service
      const itemsForService = items.map(item => ({
        productId: item.product_id,
        productTypeId: item.product_type_id,
        quantity: item.quantity
      }))

      // Format end date if applicable
      const formattedEndDate = endType === 'until_date' && endDate
        ? toLocalDateString(endDate)
        : null

      // Generate name if not provided
      const orderName = name.trim() || generateOrderName(days, items, products)

      // 1. Create the recurring order
      const recurringOrder = await createRecurringOrder(
        {
          customerId: effectiveCustomerId,
          branchId: selectedBranchId || null,
          name: orderName,
          daysOfWeek: days,
          endType,
          endDate: formattedEndDate,
          maxOccurrences: endType === 'after_count' ? occurrences : null
        },
        itemsForService
      )

      console.log('[CreateRecurringOrderDialog] Created order:', recurringOrder.id)

      // 2. Generate instances for 6 months
      await generateInstances(recurringOrder.id, 6)
      console.log('[CreateRecurringOrderDialog] Generated instances')

      toast.success('Recurring order created', {
        description: 'Deliveries scheduled for the next 6 months'
      })

      // Success - close dialog and notify parent
      onCreated?.()
      onOpenChange(false)
    } catch (err) {
      console.error('[CreateRecurringOrderDialog] Error creating:', err)
      toast.error('Failed to create recurring order', {
        description: err.message || 'Please try again'
      })
    } finally {
      setSaving(false)
    }
  }

  /**
   * Handle cancel/close
   */
  const handleClose = () => {
    if (!saving) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Recurring Order</DialogTitle>
          <DialogDescription>
            Set up a recurring order that will automatically generate deliveries on a schedule.
          </DialogDescription>
        </DialogHeader>

        {loadingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading...</span>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Customer Selector - only show if no customerId prop provided */}
            {!customerId && (
              <div className="space-y-1">
                <Label className="text-sm font-medium">Customer</Label>
                <Combobox
                  value={selectedCustomerId}
                  onChange={handleCustomerChange}
                  options={customers.map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Select customer..."
                  searchPlaceholder="Search customers..."
                  emptyMessage="No customers found."
                  disabled={saving}
                />
              </div>
            )}

            {/* Branch Selector - only show if customer selected and has multiple branches */}
            {(customerId || selectedCustomerId) && branches.length > 1 && (
              <div className="space-y-1">
                <Label htmlFor="branch-select" className="text-sm font-medium">Branch</Label>
                <Select
                  value={selectedBranchId}
                  onValueChange={setSelectedBranchId}
                  disabled={saving}
                >
                  <SelectTrigger id="branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Order Name */}
            <div className="space-y-1">
              <Label htmlFor="order-name" className="text-sm font-medium">Order Name</Label>
              <Input
                id="order-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Weekly Delivery"
                disabled={saving}
              />
            </div>

            {/* Schedule Options */}
            <RecurringOrderOptions
              selectedDays={days}
              onDaysChange={setDays}
              endType={endType}
              onEndTypeChange={setEndType}
              endDate={endDate}
              onEndDateChange={setEndDate}
              occurrences={occurrences}
              onOccurrencesChange={setOccurrences}
            />

            {/* Items Editor */}
            <div className="border-t pt-4">
              <ItemsEditor
                items={items}
                onChange={setItems}
                products={products}
                productTypes={productTypes}
                disabled={saving}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={saving || loadingData}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Recurring Order'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
